import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cliente, esAdmin } from '@/lib/cliente';
import type { PreviewPedidoEnsambladoBC, RespuestaApi, SolicitudPedidoBC } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ExternalLink, Send, Stethoscope, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * Modal para solicitar el envío de una colada a BC (Pedido de Ensamblado).
 *
 * Flujo:
 *   1. Al abrir, llama a /api/coladas/{id}/pedido-bc/preview para mostrar
 *      los datos resueltos (item M10, recurso EQ, cantidad m³) y detectar
 *      bloqueantes (sin receta_bc, sin obra, cantidad 0, ya tiene pedido).
 *   2. Operador edita el almacén destino (lo único que NO se puede
 *      deducir automático) y opcionalmente la fecha.
 *   3. Confirma → POST a /api/coladas/{id}/pedido-bc, que YA NO llama a BC
 *      directo: crea una solicitud pendiente de autorización.
 *   4. Un Ingeniero de Proyecto la aprueba (recién ahí se envía a BC de
 *      verdad, desde `/admin/pedidos-bc`) o la rechaza con motivo.
 *
 * Si el preview tiene bloqueantes, el botón queda deshabilitado y el modal
 * muestra los motivos. El operador resuelve antes (asignar obra, mapear
 * receta, etc.) y reintenta.
 */

interface Props {
  abierto: boolean;
  cerrar: () => void;
  idColada: number;
  /** Almacén destino sugerido (ej: del último pedido de la misma obra). */
  almacenSugerido?: string | null;
  /** Callback tras crear la solicitud, para que el padre invalide caches. */
  onSolicitado?: () => void;
}

export function ModalCrearPedidoBC({
  abierto,
  cerrar,
  idColada,
  almacenSugerido,
  onSolicitado,
}: Props) {
  const qc = useQueryClient();

  const previewQ = useQuery<RespuestaApi<PreviewPedidoEnsambladoBC>>({
    queryKey: ['pedido-bc-preview', idColada],
    queryFn: () => cliente.coladas.previewPedidoBC(idColada),
    enabled: abierto && idColada > 0,
    staleTime: 60_000,
  });

  const preview = previewQ.data?.data ?? null;

  const [almacenDestino, setAlmacenDestino] = useState('');
  const [fecha, setFecha] = useState('');
  const [numeroDocumentoExterno, setNumeroDocumentoExterno] = useState('');

  // Al abrir o cargar el preview, prellenamos los campos editables con
  // defaults inteligentes:
  //   - Almacén destino: el works_no de la obra (suele coincidir 1:1 con
  //     el código de almacén en BC — ej. obra "VN-L.25" → almacén "VN-L.25").
  //     `almacenSugerido` prop tiene precedencia si el caller lo pasa.
  //   - Fecha de registro: fecha de la colada (no "hoy"), para que el
  //     pedido BC quede contable en el día real del colado.
  useEffect(() => {
    if (!abierto) return;
    if (preview) {
      setAlmacenDestino(almacenSugerido ?? preview.obra_works_no ?? '');
      setFecha(preview.fecha_colada.slice(0, 10));
      setNumeroDocumentoExterno(`Colada #${preview.codigo_interno_colada}`);
    }
  }, [abierto, preview, almacenSugerido]);

  const solicitarMut = useMutation<RespuestaApi<SolicitudPedidoBC>, Error, void>({
    mutationFn: () =>
      cliente.coladas.solicitarPedidoBC(idColada, {
        codigo_almacen_destino: almacenDestino.trim().toUpperCase(),
        fecha_registro: fecha || undefined,
        numero_documento_externo: numeroDocumentoExterno.trim() || undefined,
      }),
    onSuccess: (r) => {
      if (r.data) {
        toast.success('Solicitud de envío a BC enviada', {
          description: 'Pendiente de autorización de un Ingeniero de Proyecto.',
        });
        // Invalida la colada y el listado para reflejar el estado "pendiente".
        qc.invalidateQueries({ queryKey: ['colada', idColada] });
        qc.invalidateQueries({ queryKey: ['coladas'] });
        onSolicitado?.();
        cerrar();
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido al solicitar el envío a BC');
      }
    },
    onError: (e) => {
      toast.error('Falló la solicitud de envío a BC', { description: e.message });
    },
  });

  const tieneBloqueantes = (preview?.bloqueantes.length ?? 0) > 0;
  const yaExiste = preview?.numero_pedido_bc_existente !== null && preview !== null;
  const almacenValido = almacenDestino.trim().length > 0;
  const puedeEnviar =
    preview !== null && !tieneBloqueantes && !yaExiste && almacenValido && !solicitarMut.isPending;

  // Diagnóstico (solo admin). Llama los 3 endpoints en serie y muestra el
  // resultado bajo el footer. Útil cuando BC tira 401 sin pista clara.
  const [diagnostico, setDiagnostico] = useState<unknown | null>(null);
  const [diagCargando, setDiagCargando] = useState(false);
  const mostrarDiagnostico = esAdmin();
  const correrDiagnostico = async () => {
    setDiagCargando(true);
    setDiagnostico(null);
    try {
      const config = await cliente.diagnosticoBC.config();
      const token = await cliente.diagnosticoBC.token();
      const companies = await cliente.diagnosticoBC.companies();
      const assembly = await cliente.diagnosticoBC.assembly();
      const items = await cliente.diagnosticoBC.items();
      // Prueba puntual: si esta colada ya tiene producto BC mapeado, intenta
      // leer su BOM de ensamblado (para armar más adelante las líneas de
      // materiales reales — hoy no sabemos si BC expone eso vía OData).
      const bom = preview?.codigo_producto_bc
        ? await cliente.diagnosticoBC.bom(preview.codigo_producto_bc)
        : { info: 'Esta colada no tiene producto BC mapeado — no se probó BOM.' };
      setDiagnostico({ config, token, companies, assembly, items, bom });
    } catch (e) {
      setDiagnostico({ error: (e as Error).message });
    } finally {
      setDiagCargando(false);
    }
  };

  return (
    <Dialog
      open={abierto}
      onOpenChange={(o) => {
        if (!o && !solicitarMut.isPending) cerrar();
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Solicitar envío a BC</DialogTitle>
          <DialogDescription>
            Queda pendiente de autorización de un Ingeniero de Proyecto — recién al aprobar se crea
            el pedido en Business Central (BC asigna un N° automático PENS y arma las líneas de
            materiales desde la BOM del producto).
          </DialogDescription>
        </DialogHeader>

        {previewQ.isLoading && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Cargando datos de la colada…
          </div>
        )}

        {previewQ.error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Error al cargar la vista previa: {previewQ.error.message}
          </div>
        )}

        {preview && (
          <div className="space-y-4">
            {yaExiste && preview.numero_pedido_bc_existente && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                <div className="flex items-center gap-2 font-medium text-amber-900">
                  <AlertTriangle className="h-4 w-4" />
                  Esta colada ya tiene asignado el pedido{' '}
                  <span className="font-mono">{preview.numero_pedido_bc_existente}</span>
                </div>
                <p className="mt-1 text-xs text-amber-800">
                  Para re-generar, primero limpiá el N° desde la pantalla de detalle (o anulalo en
                  BC).
                </p>
              </div>
            )}

            {tieneBloqueantes && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
                <div className="mb-1 flex items-center gap-2 font-medium text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  No se puede crear el pedido todavía:
                </div>
                <ul className="ml-5 list-disc text-xs">
                  {preview.bloqueantes.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Datos resueltos de la colada (read-only) */}
            <section className="rounded-md border bg-muted/30 p-3">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Datos resueltos de la colada
              </h3>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                <Campo label="Colada">
                  #{preview.codigo_interno_colada}
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({new Date(preview.fecha_colada).toLocaleDateString('es-CR')})
                  </span>
                </Campo>
                <Campo label="Cantidad (m³)">
                  <span className="font-mono">{preview.cantidad_m3.toFixed(2)}</span>
                </Campo>
                <Campo label="Producto BC (M10)">
                  {preview.codigo_producto_bc ? (
                    <span className="font-mono">{preview.codigo_producto_bc}</span>
                  ) : (
                    <span className="italic text-destructive">— sin mapear</span>
                  )}
                  {preview.descripcion_producto_bc && (
                    <div className="text-xs text-muted-foreground">
                      {preview.descripcion_producto_bc}
                    </div>
                  )}
                </Campo>
                <Campo label="Recurso (EQ)">
                  {preview.codigo_recurso_bc ? (
                    <span className="font-mono">{preview.codigo_recurso_bc}</span>
                  ) : (
                    <span className="italic text-muted-foreground">—</span>
                  )}
                  {preview.recurso_bc_descripcion && (
                    <div className="text-xs text-muted-foreground">
                      {preview.recurso_bc_descripcion}
                    </div>
                  )}
                </Campo>
                <Campo label="Obra" className="sm:col-span-2">
                  {preview.obra_works_no ? (
                    <>
                      <span className="font-mono">{preview.obra_works_no}</span>
                      {preview.obra_display_name && (
                        <span className="ml-2 text-muted-foreground">
                          — {preview.obra_display_name}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="italic text-destructive">— sin obra asignada</span>
                  )}
                </Campo>
              </dl>
            </section>

            {/* Inputs editables del operador */}
            {!yaExiste && !tieneBloqueantes && (
              <section className="space-y-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="alm-dest">
                    Almacén destino <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="alm-dest"
                    value={almacenDestino}
                    onChange={(e) => setAlmacenDestino(e.target.value)}
                    placeholder="VN-L.32, NV-7.24, etc."
                    className="font-mono uppercase"
                    maxLength={20}
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    Almacén de la obra donde se va a consumir el concreto. Las líneas de materiales
                    quedan en <span className="font-mono">ALM-GRAL</span> automáticamente.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label htmlFor="fecha-reg">Fecha de registro</Label>
                    <Input
                      id="fecha-reg"
                      type="date"
                      value={fecha}
                      onChange={(e) => setFecha(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="doc-ext">N° documento externo</Label>
                    <Input
                      id="doc-ext"
                      value={numeroDocumentoExterno}
                      onChange={(e) => setNumeroDocumentoExterno(e.target.value)}
                      placeholder="Colada #1234"
                      maxLength={50}
                    />
                  </div>
                </div>

                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                  <div className="flex items-center gap-1.5 font-medium">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    BC generará automáticamente:
                  </div>
                  <ul className="ml-5 mt-1 list-disc">
                    <li>El N° del pedido (No. Series PENS####)</li>
                    <li>Las líneas de materiales desde la BOM del producto</li>
                    <li>La línea del recurso ({preview.codigo_recurso_bc ?? '—'})</li>
                  </ul>
                </div>
              </section>
            )}
          </div>
        )}

        {/* Diagnóstico admin-only — útil cuando BC tira 401 sin pista. */}
        {mostrarDiagnostico && diagnostico !== null && (
          <section className="mt-2 rounded-md border bg-zinc-950 p-3 text-xs text-zinc-100">
            <div className="mb-1 font-semibold text-amber-300">Diagnóstico BC (admin)</div>
            <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap break-all text-[10px] leading-relaxed">
              {JSON.stringify(diagnostico, null, 2)}
            </pre>
          </section>
        )}

        <DialogFooter className="gap-2">
          {mostrarDiagnostico && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={correrDiagnostico}
              disabled={diagCargando}
              className="gap-1.5 mr-auto"
              title="Llama config + companies + AssemblyOrder + Item y muestra los resultados crudos"
            >
              <Stethoscope className="h-4 w-4" />
              {diagCargando ? 'Diagnosticando…' : 'Diagnóstico BC'}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={cerrar}
            disabled={solicitarMut.isPending}
            className="gap-1.5"
          >
            <X className="h-4 w-4" /> Cancelar
          </Button>
          {preview?.numero_pedido_bc_existente && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                window.open('https://businesscentral.dynamics.com/', '_blank');
              }}
              className="gap-1.5"
            >
              <ExternalLink className="h-4 w-4" /> Abrir BC
            </Button>
          )}
          <Button
            type="button"
            onClick={() => solicitarMut.mutate()}
            disabled={!puedeEnviar}
            className="gap-1.5"
          >
            <Send className="h-4 w-4" />
            {solicitarMut.isPending ? 'Enviando…' : 'Solicitar envío a BC'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Campo({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}
